import { ENEMY_TEMPLATES, type EnemyTemplate } from '../data/enemies';
import {
  type DungeonMutationId,
  scaleEnemyStat,
} from '../data/dungeonProgression';
import type { SeededRng } from './rng';
import type { EnemyIntention, EnemyState } from './types';

export function getEnemyTemplate(templateId: string): EnemyTemplate {
  const template = ENEMY_TEMPLATES[templateId];
  if (!template) {
    throw new Error(`Unknown enemy template: ${templateId}`);
  }
  return template;
}

export function rollEnemyIntention(
  template: EnemyTemplate,
  rng: SeededRng,
): EnemyIntention {
  const total = template.intentions.reduce((sum, entry) => sum + entry.weight, 0);
  if (total <= 0) {
    throw new Error(`Enemy template ${template.id} has no intentions`);
  }
  let roll = rng.nextInt(1, total);
  for (const entry of template.intentions) {
    roll -= entry.weight;
    if (roll <= 0) {
      return { ...entry.intention };
    }
  }
  const fallback = template.intentions[template.intentions.length - 1]!;
  return { ...fallback.intention };
}

export interface CreateEnemyOptions {
  dungeonLevel?: number;
  mutation?: DungeonMutationId;
}

function scaleIntention(
  intention: EnemyIntention,
  dungeonLevel: number,
  mutation: DungeonMutationId,
): EnemyIntention {
  return {
    ...intention,
    damage: scaleEnemyStat(intention.damage, dungeonLevel, mutation, 'damage'),
    guardAmount: scaleEnemyStat(
      intention.guardAmount,
      dungeonLevel,
      mutation,
      'guard',
    ),
  };
}

export function createEnemyFromTemplate(
  templateId: string,
  instanceId: string,
  rng: SeededRng,
  options: CreateEnemyOptions = {},
): EnemyState {
  const template = getEnemyTemplate(templateId);
  const dungeonLevel = options.dungeonLevel ?? 1;
  const mutation = options.mutation ?? 'standard';
  const maxHp = Math.max(
    1,
    scaleEnemyStat(template.maxHp, dungeonLevel, mutation, 'hp'),
  );
  const intention = scaleIntention(
    rollEnemyIntention(template, rng),
    dungeonLevel,
    mutation,
  );
  return {
    id: instanceId,
    name: template.name,
    maxHp,
    hp: maxHp,
    shield: 0,
    templateId,
    intention,
    statuses: [],
  };
}

export function refreshEnemyIntention(
  enemy: EnemyState,
  rng: SeededRng,
  options: CreateEnemyOptions = {},
): EnemyState {
  const template = getEnemyTemplate(enemy.templateId);
  const dungeonLevel = options.dungeonLevel ?? 1;
  const mutation = options.mutation ?? 'standard';
  return {
    ...enemy,
    intention: scaleIntention(
      rollEnemyIntention(template, rng),
      dungeonLevel,
      mutation,
    ),
  };
}

export function livingEnemies(enemies: EnemyState[]): EnemyState[] {
  return enemies.filter((e) => e.hp > 0);
}
